import React, { useEffect, useMemo, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { galleryImages } from '../data/mockData';

gsap.registerPlugin(ScrollTrigger);

const FILTERS = ['Todos', 'Comedores', 'Sillas & Bancas', 'Sala', 'Recámaras', 'Exterior', 'Consolas & TV', 'Oficina', 'Equipales', 'Accesorios'];

const WA_LINK = 'https://wa.me/523338283729';

export default function Galeria() {
  const [activeFilter, setActiveFilter] = useState('Todos');
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const gridRef = useRef(null);
  const heroRef = useRef(null);

  const filtered = useMemo(
    () => activeFilter === 'Todos'
      ? galleryImages
      : galleryImages.filter((img) => img.category === activeFilter),
    [activeFilter]
  );

  // Hero entrance
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.hero-title',
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.9, ease: 'power3.out', delay: 0.15 }
      );
      gsap.fromTo(
        '.hero-sub',
        { opacity: 0, y: 24 },
        { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out', delay: 0.35 }
      );
      gsap.fromTo(
        '.hero-badges',
        { opacity: 0, y: 16 },
        { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out', delay: 0.55 }
      );
    }, heroRef);
    return () => ctx.revert();
  }, []);

  // Grid scroll reveals — re-run on filter change
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    // Kill any existing ScrollTriggers for gallery items
    ScrollTrigger.getAll()
      .filter((t) => t.vars?.id === 'gallery-reveal')
      .forEach((t) => t.kill());

    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.gallery-item',
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          stagger: 0.06,
          duration: 0.55,
          ease: 'power3.out',
          scrollTrigger: {
            id: 'gallery-reveal',
            trigger: '.gallery-grid',
            start: 'top 85%',
          },
        }
      );
    }, gridRef);
    return () => ctx.revert();
  }, [activeFilter]);

  // Lightbox keyboard / escape
  useEffect(() => {
    if (!lightbox.open) return;
    const handler = (e) => {
      if (e.key === 'Escape') setLightbox({ open: false, index: 0 });
      if (e.key === 'ArrowRight')
        setLightbox((prev) => ({
          open: true,
          index: (prev.index + 1) % filtered.length,
        }));
      if (e.key === 'ArrowLeft')
        setLightbox((prev) => ({
          open: true,
          index: (prev.index - 1 + filtered.length) % filtered.length,
        }));
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox.open, filtered.length]);

  const openLightbox = (index) => setLightbox({ open: true, index });
  const closeLightbox = () => setLightbox({ open: false, index: 0 });
  const prevImage = (e) => {
    e.stopPropagation();
    setLightbox((prev) => ({
      open: true,
      index: (prev.index - 1 + filtered.length) % filtered.length,
    }));
  };
  const nextImage = (e) => {
    e.stopPropagation();
    setLightbox((prev) => ({
      open: true,
      index: (prev.index + 1) % filtered.length,
    }));
  };

  const currentImage = filtered[lightbox.index];

  return (
    <>
      {/* ── Hero ────────────────────────────────────────────────────── */}
      <section
        ref={heroRef}
        className="relative flex items-center justify-center overflow-hidden"
        style={{ minHeight: '65vh' }}
      >
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src="https://imagedelivery.net/xaKlCos5cTg_1RWzIu_h-A/fb4fd00d-9b0b-43f3-46b9-256a815c9b00/publicContain"
            alt="Galería Del Sol Muebles"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-wood/70 via-wood/55 to-wood/80" />
        </div>

        {/* Content */}
        <div className="relative z-10 text-center px-6 py-20">
          <h1
            className="hero-title font-serif text-display-sm md:text-display text-white mb-5 opacity-0"
            style={{ letterSpacing: '0.05em' }}
          >
            Nuestros Proyectos
          </h1>
          <p className="hero-sub font-sans text-sm md:text-base text-cream/70 max-w-xl mx-auto tracking-wide mb-10 opacity-0">
            Cada pieza es única — hecha a mano con maderas premium mexicanas
          </p>
          <div className="hero-badges flex flex-wrap justify-center gap-3 opacity-0">
            {['Hecho en México', 'Madera Sólida', 'Diseño Premium'].map((badge) => (
              <span
                key={badge}
                className="font-sans text-[10px] tracking-[0.2em] uppercase border border-gold/50 text-gold px-4 py-1.5 rounded-sm"
              >
                {badge}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── Gallery Section ─────────────────────────────────────────── */}
      <section className="bg-cream-light py-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">

          {/* Filter Tabs */}
          <div className="flex flex-wrap gap-2 justify-start md:justify-center mb-16">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setActiveFilter(f)}
                className={`font-sans text-[10px] tracking-[0.18em] uppercase px-4 py-2 rounded-sm border transition-all duration-300 ${
                  activeFilter === f
                    ? 'bg-terracotta border-terracotta text-white'
                    : 'bg-transparent border-wood/30 text-wood hover:border-terracotta hover:text-terracotta'
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          {/* Count */}
          <p className="font-sans text-xs text-taupe tracking-widest uppercase text-center mb-10">
            Mostrando{' '}
            <span className="text-wood font-semibold">{filtered.length}</span>{' '}
            {filtered.length === 1 ? 'imagen' : 'imágenes'}
          </p>

          {/* Masonry Grid */}
          <div
            ref={gridRef}
            className="gallery-grid columns-1 sm:columns-2 lg:columns-3 gap-5 space-y-5"
          >
            {filtered.map((img, i) => (
              <div
                key={img.id}
                className="gallery-item break-inside-avoid overflow-hidden rounded-sm shadow-md cursor-zoom-in group relative"
                onClick={() => openLightbox(i)}
                style={{ opacity: 0 }}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className={`w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105 ${['aspect-[4/5]', 'aspect-square', 'aspect-[3/4]'][i % 3]}`}
                />
                {/* Overlay */}
                <div className="absolute inset-0 flex items-end bg-gradient-to-t from-wood/85 via-wood/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400">
                  <div className="p-5 translate-y-3 group-hover:translate-y-0 transition-transform duration-400">
                    <span className="block font-serif text-lg text-white leading-snug mb-1">
                      {img.alt}
                    </span>
                    <span className="inline-block font-sans text-[9px] tracking-[0.22em] uppercase bg-gold/90 text-wood px-2.5 py-0.5 rounded-sm">
                      {img.category}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Empty state */}
          {filtered.length === 0 && (
            <div className="text-center py-20">
              <p className="font-serif text-2xl text-wood mb-4">Sin resultados</p>
              <button
                onClick={() => setActiveFilter('Todos')}
                className="font-sans text-sm text-terracotta underline underline-offset-4"
              >
                Ver todos →
              </button>
            </div>
          )}

          {/* WhatsApp CTA */}
          <div className="mt-24 text-center max-w-xl mx-auto">
            <div className="w-10 h-px bg-gold mx-auto mb-10" />
            <h3 className="font-serif text-3xl md:text-4xl text-wood mb-4">
              ¿Te inspiró alguna pieza?
            </h3>
            <p className="font-sans text-sm text-taupe mb-10 leading-relaxed">
              Cotiza tu versión personalizada — maderas, dimensiones y acabados a tu medida.
            </p>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-sans font-semibold text-sm tracking-wide px-8 py-4 rounded-sm transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
              </svg>
              Cotizar mi Mueble por WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* ── Lightbox ────────────────────────────────────────────────── */}
      {lightbox.open && currentImage && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.92)' }}
          onClick={closeLightbox}
        >
          {/* Close */}
          <button
            onClick={closeLightbox}
            className="absolute top-5 right-6 text-white/60 hover:text-white text-4xl font-light leading-none transition-colors z-10"
            aria-label="Cerrar"
          >
            &times;
          </button>

          {/* Prev */}
          <button
            onClick={prevImage}
            className="absolute left-4 md:left-8 text-white/60 hover:text-white transition-colors z-10 p-3 rounded-full hover:bg-white/10"
            aria-label="Anterior"
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          {/* Image */}
          <div
            className="flex flex-col items-center max-w-[90vw]"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={currentImage.src}
              alt={currentImage.alt}
              className="max-h-[82vh] max-w-[85vw] object-contain rounded-sm shadow-2xl"
            />
            <div className="mt-4 text-center">
              <span className="font-serif text-lg text-white/90">{currentImage.alt}</span>
              <span className="block font-sans text-[10px] tracking-[0.2em] uppercase text-gold mt-1">
                {currentImage.category}
              </span>
            </div>
          </div>

          {/* Next */}
          <button
            onClick={nextImage}
            className="absolute right-4 md:right-8 text-white/60 hover:text-white transition-colors z-10 p-3 rounded-full hover:bg-white/10"
            aria-label="Siguiente"
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>

          {/* Counter */}
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 font-sans text-xs text-white/40 tracking-widest">
            {lightbox.index + 1} / {filtered.length}
          </div>
        </div>
      )}
    </>
  );
}
